import React, { FC, PropsWithChildren, ReactNode, useId, useRef } from 'react'
import clsx from 'clsx'

import useDialogFocus from '../../hooks/useDialogFocus'

import { ModalDisplayMode } from './Modal'
import ModalHeader, { ModalHeaderProps } from './ModalHeader'
import styles from './ModalCard.module.scss'

type ModalCardProps = PropsWithChildren<{
  mobileModalMode?: ModalDisplayMode
  onClose?: () => void
  title?: string
  className?: string
  CustomModalHeader?: FC<ModalHeaderProps>
  modalHeaderClassName?: string
  eyebrow?: ReactNode
  subtitle?: ReactNode
  footer?: ReactNode
}>

export default function ModalCard({
  children,
  className,
  mobileModalMode = 'modal',
  onClose,
  title,
  CustomModalHeader = ModalHeader,
  modalHeaderClassName,
  eyebrow,
  subtitle,
  footer,
}: ModalCardProps) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)

  useDialogFocus(dialogRef)

  const shouldShowHeader = title || onClose || eyebrow || subtitle

  const bottomSheet = mobileModalMode === 'bottom-sheet'
  const fullScreen = mobileModalMode === 'full-screen'
  const hasFooter = !!footer
  // Only the default header is known to put `titleId` on its heading; a
  // custom one may not, so the dialog is named by its title text instead
  const isDefaultHeader = CustomModalHeader === ModalHeader

  return (
    <div
      ref={dialogRef}
      className={clsx(
        styles.modalContent,
        {
          [styles.bottomSheet]: bottomSheet,
          [styles.fullScreen]: fullScreen,
          [styles.hasFooter]: hasFooter,
        },
        className,
      )}
      role="dialog"
      aria-modal
      tabIndex={-1}
      aria-labelledby={title && isDefaultHeader ? titleId : undefined}
      aria-label={title && !isDefaultHeader ? title : undefined}
    >
      {shouldShowHeader && (
        <div className={clsx(styles.modalHeader, modalHeaderClassName)}>
          <CustomModalHeader
            title={title}
            titleId={titleId}
            onClose={onClose}
            eyebrow={eyebrow}
            subtitle={subtitle}
          />
        </div>
      )}
      {hasFooter ? (
        <>
          <div className={styles.modalBody}>{children}</div>
          <div className={styles.modalFooter}>{footer}</div>
        </>
      ) : (
        children
      )}
    </div>
  )
}
